import React from 'react';
import { Link2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Form, FormFieldSet } from '@epoch-ui/components/Form';
import {
  ActionButtonGroupField,
  ActionButtonGroupItem,
  ActionButtonGroupItemIcon,
  ActionButtonGroupItemLabel
} from '@epoch-ui/components/Form/fields/ActionButtonGroupField/ActionButtonGroupField';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { SLACK_REVAMP } from 'constants/featureFlags';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import {
  updateSlackSettings,
  UpdateSlackSettingsInputType
} from '@event-editor/api/update-slack-settings';

import {
  getSlackSettingsSchema,
  SlackSettingsFormInput,
  SlackSettingsFormValues,
  convertDBToFormData
} from './slackSettingsSchema';
import { CreateNewChannelComponent } from './CreateNewChannelComponent';
import { ConnectExistingChannelComponent } from './ConnectExistingChannelComponent';

interface SlackSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SlackSettingsModal: React.FC<SlackSettingsModalProps> = ({ isOpen, onClose }) => {
  const event = useEditableEvent();
  const queryClient = useQueryClient();
  const isSlackRevampEnabled = useFeatureFlag(SLACK_REVAMP);
  const { showToast } = useToaster();

  const methods = useForm<SlackSettingsFormInput>({
    resolver: zodResolver(
      getSlackSettingsSchema(
        !!isSlackRevampEnabled,
        event.datetimeScheduled,
        event.timezoneCode,
        event.slackChannelDate
      )
    ),
    values: convertDBToFormData(event, event.timezoneCode)
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
    reset
  } = methods;

  const createSlackChannel = watch('createSlackChannel');

  const updateSettingsMutation = useMutation({
    mutationFn: (variables: UpdateSlackSettingsInputType) => updateSlackSettings(variables),
    onSuccess: (result: any, variables) => {
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                ...result.updateSlackSettings.event
              }
            }
          : oldData
      );
      onClose();
    },
    onError: (error) => {
      console.error('Error updating Slack settings:', error);
      showToast('Failed to update Slack settings. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const onModalSubmit = (values: SlackSettingsFormValues): void => {
    if (!event.id) return;
    updateSettingsMutation.mutate({ eventId: event.id, ...values });
  };

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent size="sm" data-testid="slack-settings-modal">
        <ModalHeader className="tw-mb-5">
          <ModalTitle>Slack Channel</ModalTitle>
          <ModalDescription>
            Connect a Slack channel if you want guests to automatically join a channel when they
            register.
          </ModalDescription>
        </ModalHeader>

        <Form {...methods}>
          <form
            onSubmit={handleSubmit(onModalSubmit, (errors) => {
              console.error('Slack settings validation failed:', errors);
              const firstError = Object.values(errors).find((error) => error?.message);
              showToast(
                String(firstError?.message ?? 'Please review the Slack channel settings.'),
                {
                  variant: ToasterVariant.ERROR
                }
              );
            })}
            className="tw-flex tw-flex-col"
          >
            <FormFieldSet>
              <ActionButtonGroupField
                name="createSlackChannel"
                className="tw-my-0 tw-gap-0"
                onValueChange={(value: string) => {
                  reset({ createSlackChannel: value, slackChannelAutoarchive: false });
                }}
              >
                <ActionButtonGroupItem
                  value="true"
                  className={cn(createSlackChannel ? 'tw-mb-4' : 'tw-mb-3')}
                >
                  <ActionButtonGroupItemIcon>
                    <Plus className="tw-h-6 tw-w-6" />
                  </ActionButtonGroupItemIcon>
                  <ActionButtonGroupItemLabel>Create new channel</ActionButtonGroupItemLabel>
                </ActionButtonGroupItem>
                {createSlackChannel === 'true' && <CreateNewChannelComponent />}
                <ActionButtonGroupItem
                  value="false"
                  className={cn(createSlackChannel ? 'tw-mb-4' : 'tw-mb-0')}
                >
                  <ActionButtonGroupItemIcon>
                    <Link2 className="tw-h-6 tw-w-6" />
                  </ActionButtonGroupItemIcon>
                  <ActionButtonGroupItemLabel>Connect existing channel</ActionButtonGroupItemLabel>
                </ActionButtonGroupItem>
                {createSlackChannel === 'false' && <ConnectExistingChannelComponent />}
              </ActionButtonGroupField>
            </FormFieldSet>

            <ModalFooter className="tw-mt-5">
              <Button
                size="sm"
                className="tw-h-10 tw-w-full tw-rounded-lg tw-bg-primary-blue tw-text-white"
                type="submit"
                disabled={updateSettingsMutation.isPending || isSubmitting}
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
};
